import { useEffect, useRef, useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import MessageToster from "../component/MessageToster";
import Loader from "../component/Loader";
import { formatDateDDMMYYYY } from "../utils/dateUtils";
import { BASE_URL } from "../api";
import { exportToExcel } from "../utils/exportData";
import ExportButton from "../component/ExportButton";
import { useHandleError } from "../hooks/useHandleError";
import { getApiHeaders } from "../utils/helpers";
import CommonTable from "../component/CommonTable";
import dayjs from "dayjs";
import MobileFilter from "../component/MobileFilter";

const formatDateTime = (dateString) => {
  if (!dateString || dateString === "-") return "-";
  const dateStr = dateString.toString().split(".")[0];
  const dateObj = new Date(dateStr);
  if (isNaN(dateObj.getTime())) return dateString;

  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  const seconds = String(dateObj.getSeconds()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

export default function TradeHistory() {
  const handleError = useHandleError();
  const today = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  const weekAgoDate = oneWeekAgo.toISOString().split("T")[0];
  const mid = sessionStorage.getItem("mid");

  const [loading, setLoading] = useState(false);
  const [tradeData, setTradeData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedDates, setAppliedDates] = useState({
    fromDate: weekAgoDate,
    toDate: today,
  });
  const [accounts, setAccounts] = useState([]);
  const accountCacheRef = useRef({
    live: null,
    demo: null
  });
  const [filters, setFilters] = useState({
    accountType: "demo",
    fromDate: weekAgoDate,
    toDate: today,
    tradeType: "open",
    account: "",
  });

  const clearFilters = () => {
    setFilters({
      accountType: "demo",
      fromDate: "",
      toDate: "",
      tradeType: "open",
      account: "",
    });
    setSearchTerm("");
    setSearchQuery("");
    setAppliedDates({
      fromDate: "",
      toDate: "",
    });
    setTradeData([]);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    if (name === "accountType") {
      setFilters(prev => ({ ...prev, accountType: value, account: "" }));
      setAccounts([]);
      const list = await getAllAccounts(value);
      const firstAcc = list && list.length > 0 ? list[0] : "";
      setFilters(prev => ({ ...prev, accountType: value, account: firstAcc }));
      if (firstAcc) {
        getTradeData({
          ...filters,
          accountType: value,
          account: firstAcc
        });
      }
      return;
    }

    setFilters(prev => {
      let updatedFilter = { ...prev, [name]: value };

      if (name === "fromDate" && dayjs(value).isAfter(dayjs(prev.toDate))) {
        updatedFilter.toDate = value;
      }

      if (name === "toDate" && dayjs(value).isBefore(dayjs(prev.fromDate))) {
        updatedFilter.toDate = prev.fromDate;
      }

      return updatedFilter;
    });
  };

  const getAllAccounts = async (type) => {
    if (!type) return [];

    if (accountCacheRef.current[type]) {
      setAccounts(accountCacheRef.current[type]);
      return accountCacheRef.current[type];
    }

    const url =
      type === "live"
        ? `${BASE_URL}/MT5M/real_accounts_number_list`
        : `${BASE_URL}/MT5M/demo_account_number_list`;

    try {
      setLoading(true);
      const res = await axios.get(url, { headers: getApiHeaders() });

      if (res.data.code === 200) {
        const list = res.data.data || [];

        accountCacheRef.current[type] = list;

        setAccounts(list);
        return list;

      } else {
        MessageToster("error", res.data.message || "Failed to fetch accounts");
        return [];
      }
    } catch (error) {
      handleError(error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initPage = async () => {
      const list = await getAllAccounts("demo");
      const firstAcc = list && list.length > 0 ? list[0] : "";
      setFilters(prev => ({
        ...prev,
        accountType: "demo",
        account: firstAcc
      }));
      if (firstAcc) {
        getTradeData({
          ...filters,
          accountType: "demo",
          account: firstAcc
        });
      }
    };
    initPage();
  }, []);

  const getTradeData = async (customFilters = filters) => {

    if (!customFilters.accountType) {
      return MessageToster("error", "Please select account type");
    }
    if (!customFilters.account) {
      return MessageToster("error", "Please select account number");
    }

    if (dayjs(customFilters.toDate).isBefore(dayjs(customFilters.fromDate))) {
      return MessageToster("error", "To Date cannot be earlier than From Date");
    }


    setLoading(true);
    try {

      const endpoint =
        customFilters.accountType === "live"
          ? `${BASE_URL}/account/get_live_trades`
          : `${BASE_URL}/account/get_demo_trades`;

      const params = {
        loginid: Number(customFilters.account),
        from_date: customFilters.fromDate,
        to_date: customFilters.toDate,
      };

      const res = await axios.get(endpoint, {
        params,
        headers: getApiHeaders(),
      });

      const rawData =
        Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data?.trades)
            ? res.data.trades
            : Array.isArray(res?.data?.trade_list)
              ? res.data.trade_list
              : Array.isArray(res?.data?.list)
                ? res.data.list
                : Array.isArray(res?.data)
                  ? res.data
                  : [];

      const isSuccess =
        res.data &&
        (res.data.code === 200 ||
          res.data.status === "success" ||
          res.data.status === true ||
          res.status === 200);

      if (isSuccess && rawData.length > 0) {
        setSearchQuery(searchTerm);
        setAppliedDates({
          fromDate: customFilters.fromDate,
          toDate: customFilters.toDate,
        });
        const formattedData = rawData.map((item) => ({
          Account: item.account_number ?? "-",
          Position_ID: item.position_id ?? "-",
          Ticket: item.entry_ticket ?? item.trade_id ?? "-",
          Symbol: item.symbol ?? "-",
          Volume: item.volume ?? "-",
          Trade: item.trade_status ?? "-",
          Type: item.trade_type ?? "-",
          Time: item.exit_time ?? "-",
          Open_Time: item.exit_time ?? "-",
          Price: item.entry_price ?? "-",
          Open_Price: item.entry_price ?? "-",
          "T/P": item.tp ?? "-",
          "S/L": item.sl ?? "-",
          Commission: item.commission ?? 0,
          Swap: item.swap_amount ?? 0,
          Profit: item.net_profit ?? 0,
        }));
        setTradeData(formattedData);

      } else {
        setTradeData([]);
        setAppliedDates({
          fromDate: customFilters.fromDate,
          toDate: customFilters.toDate,
        });
        if (res.data && res.data.code && res.data.code !== 200 && res.data.status !== "success" && res.data.status !== true) {
          MessageToster("error", res.data.message || "Failed to fetch trade history");
        }
      }
    }
    catch (error) {
      setTradeData([]);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = tradeData.filter((item) => {
    // Frontend Date filter: ONLY applied on Search click (via appliedDates)
    if (appliedDates.fromDate || appliedDates.toDate) {
      const exitDateStr = item.Time ? String(item.Time).split(/[T ]/)[0] : "";
      if (exitDateStr) {
        if (appliedDates.fromDate && exitDateStr < appliedDates.fromDate) return false;
        if (appliedDates.toDate && exitDateStr > appliedDates.toDate) return false;
      }
    }

    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      String(item.Account).toLowerCase().includes(query) ||
      String(item.Position_ID).toLowerCase().includes(query) ||
      String(item.Ticket).toLowerCase().includes(query) ||
      String(item.Symbol).toLowerCase().includes(query) ||
      String(item.Volume).toLowerCase().includes(query) ||
      String(item.Trade).toLowerCase().includes(query) ||
      String(item.Type).toLowerCase().includes(query) ||
      String(item.Time).toLowerCase().includes(query) ||
      String(item.Price).toLowerCase().includes(query) ||
      String(item["T/P"]).toLowerCase().includes(query) ||
      String(item["S/L"]).toLowerCase().includes(query) ||
      String(item.Commission).toLowerCase().includes(query) ||
      String(item.Swap).toLowerCase().includes(query) ||
      String(item.Profit).toLowerCase().includes(query)
    );
  });

  return (
    <>
      <div id="layout-wrapper">
        {loading &&
          <Loader />
        }
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              {/* start page title */}
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h2 className="fw-700 mb-sm-0">Trade History</h2>
                  </div>
                </div>
              </div>
              {/* end page title */}
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex flex-wrap align-items-end gap-2 mb-4 desktop-filter-row">
                        <div>
                          <label htmlFor="">From</label>
                          <input
                            className="form-control"
                            type="date"
                            onKeyDown={(e) => e.preventDefault()}
                            name="fromDate"
                            max={filters.toDate || today}
                            value={filters.fromDate}
                            onChange={handleChange}
                            id="example-date-input"
                          />
                        </div>
                        <div>
                          <label htmlFor="">To</label>
                          <input
                            className="form-control"
                            type="date"
                            onKeyDown={(e) => e.preventDefault()}
                            name="toDate"
                            min={filters.fromDate || undefined}
                            max={today}
                            value={filters.toDate}
                            onChange={handleChange}
                            id="example-date-input"
                          />
                        </div>
                        <div>
                          <label htmlFor="">Account Type</label>
                          <select
                            className="form-select"
                            name="accountType"
                            value={filters.accountType}
                            onChange={handleChange}
                          >
                            <option value="" disabled hidden>
                              Select Account Type
                            </option>
                            <option value="live">Live</option>
                            <option value="demo">Demo</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Account No</label>
                          <select
                            className="form-select"
                            name="account"
                            value={filters.account}
                            onChange={handleChange}
                            disabled={!filters.accountType}
                          >
                            <option value="" disabled hidden>
                              {accounts?.length > 0 ? "Select Account" : "No Account Found"}
                            </option>
                            {accounts?.length > 0 ? (
                              accounts.map((acc) => (
                                <option key={acc} value={acc}>
                                  {acc}
                                </option>
                              ))
                            ) : (
                              <option disabled>No accounts available</option>
                            )}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Search</label>
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light"
                            onClick={() => getTradeData(filters)}
                          >
                            Search
                          </button>
                        </div>
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light"
                            onClick={clearFilters}
                            disabled={!filters.fromDate && !filters.toDate && !filters.account && !searchTerm}
                          >
                            Clear
                          </button>
                        </div>
                        <div className="ms-auto">
                          <ExportButton
                            disabled={!filteredData || filteredData.length === 0}
                            onClick={() => { exportToExcel(filteredData, "trade_history", "trade") }}
                          />
                        </div>
                      </div>

                      <MobileFilter
                        title="History"
                        onExport={() => { exportToExcel(filteredData, "trade_history", "trade") }}
                        exportDisabled={!filteredData || filteredData.length === 0}
                      >
                        <div>
                          <label htmlFor="">From</label>
                          <input
                            className="form-control"
                            type="date"
                            onKeyDown={(e) => e.preventDefault()}
                            name="fromDate"
                            max={filters.toDate || today}
                            value={filters.fromDate}
                            onChange={handleChange}
                          />
                        </div>
                        <div>
                          <label htmlFor="">To</label>
                          <input
                            className="form-control"
                            type="date"
                            onKeyDown={(e) => e.preventDefault()}
                            name="toDate"
                            min={filters.fromDate || undefined}
                            max={today}
                            value={filters.toDate}
                            onChange={handleChange}
                          />
                        </div>
                        <div>
                          <label htmlFor="">Account Type</label>
                          <select
                            className="form-select"
                            name="accountType"
                            value={filters.accountType}
                            onChange={handleChange}
                          >
                            <option value="" disabled hidden>
                              Select Account Type
                            </option>
                            <option value="live">Live</option>
                            <option value="demo">Demo</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Account No</label>
                          <select
                            className="form-select"
                            name="account"
                            value={filters.account}
                            onChange={handleChange}
                            disabled={!filters.accountType}
                          >
                            <option value="" disabled hidden>
                              {accounts?.length > 0 ? "Select Account" : "No Account Found"}
                            </option>
                            {accounts?.length > 0 ? (
                              accounts.map((acc) => (
                                <option key={acc} value={acc}>
                                  {acc}
                                </option>
                              ))
                            ) : (
                              <option disabled>No accounts available</option>
                            )}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Search</label>
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                        <div className="d-flex gap-2 mt-2">
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light flex-grow-1"
                            onClick={() => getTradeData(filters)}
                            data-bs-dismiss="offcanvas"
                            data-bs-target="#filterOffcanvasMobile"
                          >
                            Search
                          </button>
                          <button
                            type="button"
                            className="btn btn-light waves-effect waves-light border flex-grow-1"
                            onClick={clearFilters}
                            disabled={!filters.fromDate && !filters.toDate && !filters.account && !searchTerm}
                            data-bs-dismiss="offcanvas"
                            data-bs-target="#filterOffcanvasMobile"
                          >
                            Clear
                          </button>
                        </div>
                      </MobileFilter>
                      <CommonTable
                        columns={[
                          { header: "Account", accessor: "Account" },
                          { header: "Position ID", accessor: "Position_ID" },
                          { header: "Ticket", accessor: "Ticket" },
                          { header: "Symbol", accessor: "Symbol" },
                          { header: "Volume", accessor: "Volume" },
                          {
                            header: "Trade",
                            render: (row) => {
                              if (!row.Trade || row.Trade === "-") return "-";
                              const tradeUpper = String(row.Trade).toUpperCase();
                              if (tradeUpper === "CLOSED") {
                                return <span className="badge bg-secondary-subtle text-secondary px-2 py-1 font-size-11">CLOSED</span>;
                              }
                              if (tradeUpper === "OPEN") {
                                return <span className="badge bg-primary-subtle text-primary px-2 py-1 font-size-11">OPEN</span>;
                              }
                              return row.Trade;
                            }
                          },
                          {
                            header: "Type",
                            render: (row) => {
                              if (!row.Type || row.Type === "-") return "-";
                              const typeUpper = String(row.Type).toUpperCase();
                              if (typeUpper === "BUY") {
                                return <span className="badge bg-success-subtle text-success px-2 py-1 font-size-12">BUY</span>;
                              }
                              if (typeUpper === "SELL") {
                                return <span className="badge bg-danger-subtle text-danger px-2 py-1 font-size-12">SELL</span>;
                              }
                              return row.Type;
                            }
                          },
                          {
                            header: "Time",
                            render: (row) => formatDateTime(row.Time || row.Open_Time)
                          },
                          { header: "Price", accessor: "Price" },
                          { header: "T/P", accessor: "T/P" },
                          { header: "S/L", accessor: "S/L" },
                          { header: "Commission", accessor: "Commission" },
                          { header: "Swap", accessor: "Swap" },
                          {
                            header: "Profit/Loss",
                            render: (row) => {
                              const num = Number(row.Profit);
                              if (!isNaN(num) && row.Profit !== "-") {
                                return (
                                  <span className={`fw-semibold ${num > 0 ? "text-success" : num < 0 ? "text-danger" : ""}`}>
                                    {num > 0 ? `+${row.Profit}` : row.Profit}
                                  </span>
                                );
                              }
                              return row.Profit ?? "-";
                            }
                          }
                        ]}
                        data={filteredData}
                        loading={loading}
                      />
                    </div>
                  </div>
                </div>{" "}
                {/* end col */}
              </div>
            </div>
            {/* container-fluid */}
          </div>

          {/* End Page-content */}
          <Footer />
        </div>
      </div>
    </>
  );
}